import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";

/**
 * Left navigation for the Q&A surfaces. Pinned tags ride in
 * `localStorage` for v1 — once we have a per-user watchlist column, we
 * swap the storage backend behind this same hook.
 */
export function LeftNav() {
  const watched = useWatchedTags();
  return (
    <nav className="text-sm-dense">
      <ul className="space-y-0.5">
        <NavItem to="/" label="Home" />
        <NavItem to="/questions" label="Questions" />
        <NavItem to="/tags" label="Tags" />
        {/* Users page is a stub — render disabled so the IA reads clean. */}
        <li>
          <span
            className="block px-3 py-1.5 rounded text-zinc-400 dark:text-zinc-600 cursor-not-allowed"
            title="Coming soon"
          >
            Users
          </span>
        </li>
      </ul>

      {watched.length > 0 && (
        <div className="mt-6">
          <div className="px-3 text-xs-dense font-semibold uppercase tracking-wide text-zinc-500 mb-1.5">
            Watched tags
          </div>
          <ul className="space-y-0.5">
            {watched.map((slug) => (
              <li key={slug}>
                <Link
                  to={`/tags/${encodeURIComponent(slug)}`}
                  className="block px-3 py-1 rounded text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 truncate"
                >
                  {slug}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <li>
      <NavLink
        to={to}
        end={to === "/"}
        className={({ isActive }) =>
          [
            "block px-3 py-1.5 rounded font-medium",
            isActive
              ? "bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 border-l-2 border-accent -ml-0.5 pl-[10px]"
              : "text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900",
          ].join(" ")
        }
      >
        {label}
      </NavLink>
    </li>
  );
}

const WATCHED_TAGS_KEY = "coauthor:watched_tags:v1";

/**
 * Reads / writes the user's watched tag slugs from `localStorage`.
 * Returns a sorted, deduped list. Tag pages can call `add(slug)` to
 * pin a tag from the tag detail page (wiring lives in `<TagDetail>`).
 */
export function useWatchedTags(): string[] {
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(WATCHED_TAGS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((t) => typeof t === "string")) {
        setTags(parsed.slice().sort());
      }
    } catch {
      // ignore malformed storage
    }
  }, []);
  return tags;
}

/** Toggle a tag in the localStorage watch list. Returns the new list. */
export function toggleWatchedTag(slug: string): string[] {
  let current: string[] = [];
  try {
    const raw = window.localStorage.getItem(WATCHED_TAGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) current = parsed.filter((t) => typeof t === "string");
    }
  } catch {
    /* noop */
  }
  const next = current.includes(slug)
    ? current.filter((t) => t !== slug)
    : [...current, slug];
  next.sort();
  try {
    window.localStorage.setItem(WATCHED_TAGS_KEY, JSON.stringify(next));
  } catch {
    /* noop */
  }
  return next;
}
